import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import useAuth from '@/hooks/useAuth';
import ROUTES from '@/routes/routes';
import { centsToCurrency, currencyToCents } from '@/util/number';
import { CalculateCost } from '@/util/payments';

import { JobDraftV2 } from '../../context';
import useRateRange from '../../hooks/useRateRange';
import {
  BillingAccountV2,
  LocationListItem,
  PaymentMethodsResponse,
  SkillListItem,
  SurgeRateV2,
} from '../../api/types';
import { resolveJobTimezone, nowInTimezone } from '../../utils/locationTime';
import ShiftPopup, {
  breakLabelFromMinutes,
  centeredPopupPosition,
  minutesFromBreakLabel,
  time12To24,
  time24To12,
} from '../ScheduleStep/ShiftPopup';

import ScheduleNeededBanner from './ScheduleNeededBanner';

import {
  AddSkillsButton,
  Card,
  CardHeader,
  CardTitle,
  Chevron,
  Collapsible,
  IconButton,
  JobHeaderRow,
  JobIcon,
  JobIconImg,
  JobMeta,
  JobMetaText,
  JobName,
  KeyValueRow,
  KvLabel,
  KvValue,
  LeftColumn,
  OrderHeading,
  PageShell,
  PaymentBody,
  PaymentLabel,
  PaymentMuted,
  RateInputDollar,
  RateInput,
  RateInputShell,
  RatesSection,
  RightColumn,
  ScheduleAction,
  ScheduleActions,
  ScheduleDateCell,
  ScheduleHeaderCell,
  ScheduleHeaderRow,
  ScheduleHeaderTime,
  ScheduleHeaderTimeLabel,
  ScheduleHeaderTz,
  ScheduleLabel,
  ScheduleRow,
  ScheduleRowsWrap,
  ScheduleTable,
  ScheduleTimeCell,
  ScheduleTimeMain,
  ScheduleTimeSub,
  ScheduleToggleInner,
  ScheduleToggleRow,
  ScheduleWorkersCell,
  TwoColumn,
} from './styles';

type ReviewStepProps = {
  orderType: 'GIG' | 'LTA' | null;
  jobs: JobDraftV2[];
  skills?: SkillListItem[];
  // True while `/skills` is in flight. Used to suppress the
  // "saved skill is no longer available" warning so we never false-flag
  // a job during the initial fan-out after a template apply.
  skillsLoading?: boolean;
  // True once the account-scoped `/skills` query has resolved at least
  // once, *including* a settled empty list. The skill-unavailable guard
  // keys off this rather than `skills.length > 0` so an empty account
  // skills response still flags + gates a template-loaded skill_id
  // instead of letting it through as a blank/orphaned card.
  skillsSettled?: boolean;
  paymentMethods?: PaymentMethodsResponse | null;
  paymentMethodsLoading?: boolean;
  paymentMethodsError?: string | null;
  billingAccountName?: string | null;
  // Legacy v1 heading is `Order for ${billing.customer.name}`. When the
  // wizard can resolve the parent customer (typical case), the review
  // header prefers `customerName`; otherwise we fall back to the billing
  // account label to keep the heading non-empty.
  customerName?: string | null;
  billingAccount?: BillingAccountV2 | null;
  // Billing-account locations (carry the IANA `timezone`), forwarded to
  // each JobCard so the client-admin past-time check uses the job
  // location's timezone — V1 parity.
  locations?: LocationListItem[];
  onUpdateJobs?: (jobs: JobDraftV2[]) => void;
  onAddSkill?: () => void;
  // Re-enter the wizard to edit an existing job in place (by index).
  // The orchestrator move-to-fronts that job into jobs[0] and restarts
  // at step 0, pre-populated from the job's current/template values.
  // This is the only path to give a multi-skill template's non-first
  // skills a schedule/rates (they load with `shifts: []`).
  onEditJob?: (index: number) => void;
  surgeRates?: SurgeRateV2[];
  // Aggregated validity across the editable pay-rate inputs. False when
  // any tenant-admin editable job has a pay rate below its rate-range
  // minimum, so the parent can disable the Submit button (v1 parity:
  // CustomErrorsForm on the summary blocked submit when below-min).
  onRatesValidityChange?: (valid: boolean) => void;
  // Reports whether the billing account has a `current_payment_method`.
  // False blocks Submit at the parent (v1 parity: legacy Billing form
  // disabled "Start order" when the account had no current_payment_method).
  onPaymentMethodValidityChange?: (valid: boolean) => void;
  // False when one or more repeated jobs reference a saved skill that
  // no longer resolves against the loaded `/skills` list (multi-skill
  // template applied with at least one skill since deactivated). The
  // parent disables Submit while this is false so the admin cannot
  // ship an order with an orphaned skill card.
  onSkillsValidityChange?: (valid: boolean) => void;
  // False when one or more jobs in the order carry an empty `shifts: []`.
  // The parent disables Submit while this is false so the admin cannot
  // ship an order with a job that has no schedule (typical for
  // multi-skill templates whose non-first skills were never edited).
  onShiftsValidityChange?: (valid: boolean) => void;
};

// Total hours rolls up *per-shift quantity*, not the job-level quantity.
// When shifts under the same job have different worker counts (e.g.
// 8 hrs × 1 worker + 8 hrs × 3 workers = 32 hrs, NOT 8+8=16 hrs ×
// `job.quantity`), the older "sum durations, multiply by job qty"
// approach scaled every shift by the same number and over-/under-
// counted whenever shifts diverged. Falling back to `job.quantity` for
// shifts missing their own quantity keeps legacy single-quantity orders
// behaving the same way.
const totalShiftHours = (job: JobDraftV2) =>
  (job.shifts || []).reduce((sum, shift) => {
    const start = new Date(shift.start_at).getTime();
    const end = new Date(shift.end_at).getTime();
    let diffMs = end - start;
    if (diffMs <= 0) diffMs += 24 * 3_600_000;
    const hours = diffMs / 3_600_000 - (shift.break_minutes || 0) / 60;
    const qty = shift.quantity ?? job.quantity ?? 1;
    return sum + Math.max(hours, 0) * qty;
  }, 0);

const formatDate = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
};

const formatTime = (iso: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatDateRange = (job: JobDraftV2) => {
  const shifts = job.shifts || [];
  if (shifts.length === 0) return '—';
  const sorted = [...shifts].sort((a, b) =>
    a.start_at.localeCompare(b.start_at),
  );
  const first = formatDate(sorted[0].start_at);
  const last = formatDate(sorted[sorted.length - 1].start_at);
  return first === last ? first : `${first} – ${last}`;
};

const formatCurrency = (value: number) => `$${value.toFixed(2)}`;
const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_ABBR = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

const breakLabel = (mins?: number) =>
  `${mins || 0} min${mins === 1 ? '' : 's'} break`;
const toDateLabel = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${DAY_ABBR[d.getDay()]}, ${MONTH_ABBR[d.getMonth()]} ${ordinal(d.getDate())}, ${d.getFullYear()}`;
};

const shiftHours = (startIso: string, endIso: string, breakMins?: number) => {
  const start = new Date(startIso).getTime();
  const end = new Date(endIso).getTime();
  let diffMs = end - start;
  if (diffMs <= 0) diffMs += 24 * 3_600_000;
  const hours = diffMs / 3_600_000 - (breakMins || 0) / 60;
  return Math.max(hours, 0);
};

const formatHoursLabel = (hours: number) => {
  if (hours <= 0) return '';
  const rounded = Math.round(hours * 100) / 100;
  return `(${rounded.toFixed(2)} hrs)`;
};
const centsToDollars = (value?: number) => (value || 0) / 100;

// V1 parity: the legacy flow accepts ANY truthy `current_payment_method`
// (card, ACH bank account, or check) and formats each case. V2 previously
// only formatted cards, so ACH/Check accounts fell through to the
// "no payment method" warning even though they were valid. This resolves
// all three supported cases and only returns null when there is genuinely
// no payment method on file.
const formatPaymentMethod = (
  payment: PaymentMethodsResponse | null | undefined,
) => {
  if (!payment) return null;
  const current = payment.current_payment_method;

  // Check accounts frequently have no Stripe customer (blank stripe_id),
  // so there are no cards/banks to render — surface them explicitly.
  if (typeof current === 'string' && current.toLowerCase() === 'check') {
    return 'Check';
  }

  // Card (unchanged behaviour — kept ungated on current_payment_method so
  // an account that exposes a card still displays it).
  if (payment.primary_card_label) return payment.primary_card_label;
  const card = payment.cards?.[0];
  if (card) {
    const brand = (card.brand || '').replace(/^\w/, (c) => c.toUpperCase());
    return `${brand} **** ${card.last4 || '••••'}`;
  }

  // ACH bank account.
  const bank = payment.bank_accounts?.[0];
  if (bank) {
    const name = bank.bank_name || 'Bank account';
    return bank.last4 ? `${name} **** ${bank.last4}` : name;
  }

  // The account has a current payment method on file but no Stripe-backed
  // detail to render (e.g. ACH/Check with a blank stripe_id). V1 treats
  // any truthy current_payment_method as valid — mirror that rather than
  // showing the "missing payment method" warning.
  if (current) return 'Payment method on file';
  return null;
};

const ChevronIcon = () => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    stroke="#44A735"
    strokeWidth={2}
    width={16}
    height={16}
  >
    <polyline points="5 8 10 13 15 8" />
  </svg>
);

const PlusIcon = () => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    stroke="#44A735"
    strokeWidth={2}
    width={18}
    height={18}
  >
    <line x1="10" y1="4" x2="10" y2="16" />
    <line x1="4" y1="10" x2="16" y2="10" />
  </svg>
);

const PencilIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 20 20"
    fill="none"
    stroke="#979797"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14.7 3.3a1 1 0 0 1 1.4 1.4l-9.9 9.9-2.8.7.7-2.8 9.9-9.9z" />
    <path d="M13 5l2 2" />
  </svg>
);

const TrashIcon = () => (
  <svg
    width={18}
    height={18}
    viewBox="0 0 20 20"
    fill="none"
    stroke="#979797"
    strokeWidth={1.5}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="5 7 15 7" />
    <path d="M8 7V5h4v2" />
    <rect x={6} y={7} width={8} height={9} rx={1} />
  </svg>
);

type JobCardProps = {
  job: JobDraftV2;
  index: number;
  skills?: SkillListItem[];
  skillsLoading?: boolean;
  skillsSettled?: boolean;
  defaultOpen?: boolean;
  isMarkup?: boolean;
  markupPercent?: number;
  billingAccountId?: number | null;
  // Billing-account locations (carry the IANA `timezone`). Anchors the
  // client-admin edit-popup past-time check to the job location's
  // timezone instead of the browser's — V1 parity.
  locations?: LocationListItem[];
  onUpdateJob?: (next: JobDraftV2) => void;
  // Per-job validity for the editable pay-rate input on review.
  // Reports `true` when the rate is not editable here (read-only branch)
  // or sits at/above the fetched market minimum; reports `false` when an
  // editable input has been driven below minimum.
  onValidityChange?: (index: number, valid: boolean) => void;
  // Recovery callback for the "saved skill no longer available" branch.
  // Removes this job from the order. Only wired when there is at least
  // one other job — the parent must keep the order non-empty.
  onRemoveJob?: () => void;
  // Re-enter the wizard to edit this job in place. Always wired (unlike
  // onRemoveJob, which is gated on jobs.length > 1) so even a single
  // job — e.g. a one-skill repeat whose schedule was never set — can be
  // taken back through the wizard.
  onEditJob?: () => void;
  // Per-job skill availability signal. `true` while a template-loaded
  // `skill_id` does not resolve against the available skills list. The
  // parent aggregates these flags into a single Submit gate.
  onSkillAvailabilityChange?: (index: number, unavailable: boolean) => void;
  // Per-job "has at least one shift" signal. `false` while this job
  // carries an empty `shifts: []` (typical of a multi-skill template's
  // non-first skill that never went through the schedule step). The
  // parent aggregates these into a single Submit gate so the wizard
  // cannot ship a job with no schedule.
  onShiftsValidityChange?: (index: number, valid: boolean) => void;
};

// Mirrors legacy CreateGigOrder PayRateStep / SummaryTableRevamped color rule:
// red below market minimum, orange below market max, green at/above max.
const rateColor = (payCents: number, minPay: number, maxPay: number) => {
  if (payCents < minPay) return '#DC1515';
  if (payCents < maxPay) return '#EC7F00';
  return '#45A735';
};

type EditTarget = {
  shiftIdx: number;
  dateKey: string;
  startTime: string;
  endTime: string;
  breakTime: string;
  workers: number;
} | null;

const JobCard = ({
  job,
  index,
  skills,
  skillsLoading,
  skillsSettled,
  defaultOpen,
  isMarkup,
  markupPercent,
  billingAccountId,
  locations = [],
  onUpdateJob,
  onValidityChange,
  onRemoveJob,
  onEditJob,
  onSkillAvailabilityChange,
  onShiftsValidityChange,
}: JobCardProps) => {
  const { currentAdminIsCustomerAdmin: isClientAdmin } = useAuth();
  // Legacy v1 review surface only exposes editable rate inputs on the
  // markup-revamped flow — the static/non-markup `JobRates` component
  // renders Base Pay rate / Base Bill rate as read-only text. Mirror
  // that here so tenant admins on a non-markup account see static rate
  // values on the review page rather than editable inputs. The bill
  // rate row is only shown for non-markup (see `!isMarkup` guard below)
  // so it is always read-only on review. On markup accounts V1's
  // SummaryTableRevamped renders the Pay rate input for client admins
  // too, so we don't gate edit on `!isClientAdmin` here.
  const payRateEditable = !!onUpdateJob && isMarkup;
  const [open, setOpen] = useState<boolean>(!!defaultOpen);
  const [editTarget, setEditTarget] = useState<EditTarget>(null);
  const skill = skills?.find((s) => s.id === job.skill_id) || null;
  // Templates persist `skill_id` directly, but the V2 skills endpoint
  // only returns active + requestable skills for the account's agency.
  // For a multi-skill repeat order, any saved skill that's since been
  // deactivated leaves the job with an id but no resolvable display
  // object — without intervention the card would render a generic
  // "Job N" placeholder and the user could submit it. We surface the
  // gap explicitly, expose a Replace + Remove recovery path, and gate
  // submit via `onSkillAvailabilityChange`.
  // A repeat skill is "unavailable" once the skills query has settled and
  // the saved id still doesn't resolve. The settle signal is `skillsSettled`
  // (true even for a resolved *empty* account skills list); we fall back to
  // the pre-`skillsSettled` heuristic only when the prop isn't wired (older
  // callers / unit harnesses) so behaviour is unchanged there.
  const skillsAreSettled =
    skillsSettled ?? (!skillsLoading && !!skills && skills.length > 0);
  const skillUnavailable = job.skill_id != null && skillsAreSettled && !skill;
  // True when the account genuinely returned zero requestable skills.
  // The Replace `<select>` would be empty here, so for the single-job
  // case (no Remove) we show a clear account-level block instead of an
  // empty dropdown the user can't act on.
  const noSkillsForAccount =
    skillUnavailable && skillsSettled === true && (skills?.length ?? 0) === 0;
  useEffect(() => {
    if (!onSkillAvailabilityChange) return;
    onSkillAvailabilityChange(index, skillUnavailable);
  }, [onSkillAvailabilityChange, index, skillUnavailable]);
  const skillName = skill?.display_name_en || skill?.name || `Job ${index + 1}`;
  const shiftCount = (job.shifts || []).length;
  const hours = totalShiftHours(job);
  // Multi-skill templates load each non-first skill with `shifts: []`,
  // and a user may click Add Skills and land on the Summary without ever
  // scheduling it. A shift-less job is invalid (no dates, no hours, no
  // bill rate), so we surface it on the card and gate Submit until the
  // user either edits the job to add shifts or removes it.
  const noShifts = shiftCount === 0;
  useEffect(() => {
    if (!onShiftsValidityChange) return;
    onShiftsValidityChange(index, !noShifts);
  }, [onShiftsValidityChange, index, noShifts]);
  const [payInput, setPayInput] = useState<string>(() =>
    job.pay_rate ? centsToCurrency(job.pay_rate) : '',
  );

  useEffect(() => {
    setPayInput(job.pay_rate ? centsToCurrency(job.pay_rate) : '');
  }, [job.pay_rate]);

  // Markup billing accounts get the legacy red/yellow/green coloring on
  // the pay rate input, driven by the per-skill/address rate range
  // fetched from the v2 admin REST endpoint. We route through the shared
  // useRateRange hook (React Query cache) so this JobCard, the
  // ScheduleStep, and the PayRateStep all share a single cached call
  // per (account, skill, address) tuple — multi-skill markup repeats
  // used to fan out one raw fetch per surface and per JobCard mount,
  // which is what was tripping the backend's 429 rate limit.
  //
  // `rateRangeSettled` (success or failure) closes the empty-repeated-
  // skill race: a template-seeded pay_rate below the current floor
  // would otherwise sneak past validation in the brief in-flight window
  // before the fetch lands. The hook reports `isSettled: true` when
  // disabled (any prereq null), so non-markup / incomplete jobs don't
  // permanently report rate validation as pending.
  const { range: rateRange, isSettled: rateRangeSettled } = useRateRange(
    isMarkup ? (billingAccountId ?? null) : null,
    isMarkup ? (job.skill_id ?? null) : null,
    isMarkup ? (job.address_id ?? null) : null,
  );

  const minPay = rateRange?.minimum ?? 0;
  const maxPay = rateRange?.maximum ?? 0;
  const payValueCents = Math.round(
    (Number(payInput.replace(/[$,]/g, '')) || 0) * 100,
  );
  const payColor = isMarkup
    ? rateColor(payValueCents, minPay, maxPay)
    : undefined;

  // Block submit when this job's editable pay rate sits below the
  // fetched market minimum. We compare against the committed `pay_rate`
  // rather than the live input so a mid-edit doesn't toggle the parent's
  // Submit button on every keystroke — the input's onBlur is what
  // ultimately commits + flips this flag.
  const payBelowMin =
    payRateEditable && minPay > 0 && (job.pay_rate || 0) < minPay;
  // While the rate-range fetch is still pending for a markup editable
  // job whose skill+address prereqs are set, also report invalid. This
  // closes the empty-repeated-skill window where a template-seeded rate
  // would otherwise sneak past per-job validation in the brief moment
  // before the fetch lands. Once `rateRangeSettled` flips true the
  // below-min check above takes over; if the backend returns no range,
  // `minPay` stays 0 and we fall through without false-blocking.
  const rateRangePending =
    isMarkup &&
    !rateRangeSettled &&
    billingAccountId != null &&
    job.skill_id != null &&
    job.address_id != null;
  const payInvalid = payBelowMin || (payRateEditable && rateRangePending);
  useEffect(() => {
    if (!onValidityChange) return;
    onValidityChange(index, !payInvalid);
  }, [onValidityChange, index, payInvalid]);

  const commitPayRate = (raw: string) => {
    if (!onUpdateJob) return;
    const trimmed = raw.trim();
    const cents = trimmed ? Math.round(currencyToCents(trimmed)) : 0;
    if (Number.isNaN(cents)) return;
    // Markup accounts derive cost from pay + markup_percent (legacy
    // CalculateCost). Without this, the right-rail "Total estimate" would
    // never refresh when the user edits the pay rate. Bill rate is never
    // editable on the review page (see `JobRates` in legacy v1 for the
    // static read-only parity).
    const cost = CalculateCost(trimmed || '0', markupPercent ?? 0);
    // V1 parity: every rate edit keeps `original_*` aligned with the
    // markup-derived pair. The backend re-validates markup jobs against
    // `original_pay_rate`/`original_cost_rate`; leaving them stale (or 0
    // from a never-touched quote) is what 422s with "Invalid markup
    // rates". buildCreatePayload re-derives this at submit too, but
    // keeping state coherent here also fixes the right-rail estimate and
    // any path that reads original_* before submit.
    onUpdateJob({
      ...job,
      pay_rate: cents,
      cost_rate: cost,
      original_pay_rate: cents,
      original_cost_rate: cost,
    });
  };

  const sortedShifts = [...(job.shifts || [])].sort((a, b) =>
    a.start_at.localeCompare(b.start_at),
  );

  const handleDeleteShift = (idx: number) => {
    if (!onUpdateJob) return;
    const target = sortedShifts[idx];
    if (!target) return;
    const nextShifts = (job.shifts || []).filter(
      (shift) =>
        !(shift.start_at === target.start_at && shift.end_at === target.end_at),
    );
    onUpdateJob({ ...job, shifts: nextShifts });
  };

  const handleOpenEdit = (idx: number) => {
    const shift = sortedShifts[idx];
    if (!shift) return;
    setEditTarget({
      shiftIdx: idx,
      dateKey: shift.start_at.slice(0, 10),
      startTime: time24To12(shift.start_at),
      endTime: time24To12(shift.end_at),
      breakTime: breakLabelFromMinutes(shift.break_minutes ?? 15),
      workers: shift.quantity || 1,
    });
  };

  const handleSaveEdit = (values: {
    startTime: string;
    endTime: string;
    breakTime: string;
    workers: number;
  }) => {
    if (!editTarget || !onUpdateJob) return;
    const target = sortedShifts[editTarget.shiftIdx];
    if (!target) return;
    const dateKey = editTarget.dateKey;
    const nextShifts = (job.shifts || []).map((shift) => {
      if (shift.start_at !== target.start_at || shift.end_at !== target.end_at)
        return shift;
      return {
        ...shift,
        start_at: `${dateKey}T${time12To24(values.startTime)}`,
        end_at: `${dateKey}T${time12To24(values.endTime)}`,
        break_minutes: minutesFromBreakLabel(values.breakTime),
        quantity: values.workers,
      };
    });
    onUpdateJob({ ...job, shifts: nextShifts });
    setEditTarget(null);
  };

  return (
    <Card>
      {onEditJob || onRemoveJob ? (
        // Legacy v1 parity: every job card on the Summary surface exposes
        // top-level actions (legacy's `Card actions={[...]}` in
        // CreateGigOrder/ReviewStep). Remove is gated on jobs.length > 1
        // so we never leave the wizard with an empty order. Edit re-enters
        // the wizard for this job in place — the only way to give a
        // multi-skill template's non-first skills a schedule/rates, since
        // every step reads/writes jobs[0] and those skills load with no
        // shifts.
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            padding: '12px 16px 0',
          }}
        >
          {onEditJob ? (
            <button
              type="button"
              aria-label={`Edit job ${index + 1}`}
              onClick={onEditJob}
              style={{
                height: 30,
                borderRadius: 6,
                border: '1px solid #DADADA',
                background: '#fff',
                color: '#1C7D2E',
                padding: '0 10px',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <PencilIcon />
              Edit
            </button>
          ) : null}
          {onRemoveJob ? (
            <button
              type="button"
              aria-label={`Remove job ${index + 1}`}
              onClick={onRemoveJob}
              style={{
                height: 30,
                borderRadius: 6,
                border: '1px solid #DADADA',
                background: '#fff',
                color: '#A41B1B',
                padding: '0 10px',
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <TrashIcon />
              Remove
            </button>
          ) : null}
        </div>
      ) : null}
      {skillUnavailable ? (
        <div
          role="alert"
          aria-label="Saved skill no longer available"
          style={{
            margin: '16px 24px 0',
            padding: '12px 14px',
            borderRadius: 6,
            background: '#FDECEC',
            border: '1px solid #F5B7B7',
            color: '#A41B1B',
            fontSize: 13,
            lineHeight: 1.4,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {noSkillsForAccount && !onRemoveJob ? (
            // Single-job repeat + the account returned zero requestable
            // skills: there's nothing to populate a Replace dropdown with
            // and we can't Remove the only job. Surface a clear, actionable
            // account-level block (Submit stays gated) instead of an empty
            // dropdown or a silently blank card — this is the dead-end the
            // bug calls out.
            <div>
              <strong>Saved skill is no longer available.</strong> This billing
              account has no requestable skills right now, so the skill saved
              with this template can&apos;t be replaced here. Pick a different
              billing account or repeat order, or add a requestable skill to
              this account, then start the order again.
            </div>
          ) : (
            <>
              <div>
                <strong>Saved skill is no longer available.</strong> The skill
                saved with this template can&apos;t be requested for this
                billing account anymore. Replace it with an available skill or
                remove this job before placing the order.
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                {(skills?.length ?? 0) > 0 ? (
                  <>
                    <label
                      htmlFor={`replace-skill-${index}`}
                      style={{ fontWeight: 700 }}
                    >
                      Replace with:
                    </label>
                    <select
                      id={`replace-skill-${index}`}
                      aria-label="Replace skill"
                      value=""
                      disabled={!onUpdateJob}
                      onChange={(event) => {
                        if (!onUpdateJob) return;
                        const nextId = Number(event.target.value);
                        if (!nextId) return;
                        onUpdateJob({ ...job, skill_id: nextId });
                      }}
                      style={{
                        height: 32,
                        borderRadius: 6,
                        border: '1px solid #DADADA',
                        padding: '0 8px',
                        background: '#fff',
                        color: '#1C1C1C',
                        fontSize: 13,
                      }}
                    >
                      <option value="" disabled>
                        Select a skill…
                      </option>
                      {(skills || []).map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.display_name_en || option.name}
                        </option>
                      ))}
                    </select>
                  </>
                ) : (
                  <span>
                    No requestable skills are available for this billing
                    account, so this job can&apos;t be replaced — remove it to
                    continue.
                  </span>
                )}
                {/*
                  The top-level "Remove" button on the JobHeaderRow
                  covers removal for any multi-job order, so no
                  duplicate Remove is rendered inside the alert. For
                  the noSkillsForAccount + single-job case we hit the
                  branch above which shows the account-level message.
                */}
              </div>
            </>
          )}
        </div>
      ) : null}
      {noShifts && !skillUnavailable ? (
        // A job carrying `shifts: []` cannot be submitted — there is no
        // schedule, total hours roll up to 0, and the bill estimate is
        // meaningless. We highlight the offending card and gate Submit
        // via `onShiftsValidityChange`; recovery is the top-level Edit
        // button (always wired) or Remove (when this isn't the only job
        // in the order). The card stays expanded by default for the
        // first job, so this alert sits where the user is already
        // looking, but other cards also get the same treatment when
        // they collapse — the warning lives above the header so it's
        // visible regardless of the open/closed state.
        <div
          role="alert"
          aria-label={`Job ${index + 1} has no shifts`}
          style={{
            margin: '16px 24px 0',
            padding: '12px 14px',
            borderRadius: 6,
            background: '#FDECEC',
            border: '1px solid #F5B7B7',
            color: '#A41B1B',
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          <strong>No shifts have been added to this job.</strong>{' '}
          {onRemoveJob
            ? 'Edit it to add a schedule, or remove this job before placing the order.'
            : 'Edit it to add a schedule before placing the order.'}
        </div>
      ) : null}
      {payBelowMin ? (
        // Pay-rate below-minimum has to stay visible regardless of the
        // Collapsible's open state. The detailed alert (and the editable
        // input) live inside the Schedule collapsible below, which is
        // closed by default for every job after the first — so for an
        // empty repeated skill on a markup account, the user otherwise
        // gets a disabled Submit with no visible reason. Surface a
        // hoisted banner above the header that mirrors the noShifts
        // pattern: same Submit gate (`onValidityChange` → parent
        // `reviewRatesValid`), but always seen.
        <div
          role="alert"
          aria-label={`Job ${index + 1} pay rate below minimum`}
          style={{
            margin: '16px 24px 0',
            padding: '12px 14px',
            borderRadius: 6,
            background: '#FDECEC',
            border: '1px solid #F5B7B7',
            color: '#A41B1B',
            fontSize: 13,
            lineHeight: 1.4,
          }}
        >
          <strong>Pay rate is below minimum.</strong> The minimum pay rate for
          this skill is {formatCurrency(centsToDollars(minPay))}. Edit this job
          to raise the rate before placing the order.
        </div>
      ) : null}
      <JobHeaderRow>
        <JobIcon>
          {skill?.image_url ? (
            <JobIconImg src={skill.image_url} alt="" />
          ) : (
            skillName.slice(0, 1).toUpperCase()
          )}
        </JobIcon>
        <JobMeta>
          <JobName>{skillName}</JobName>
          <JobMetaText>{formatDateRange(job)}</JobMetaText>
          <JobMetaText muted>
            {shiftCount} shift{shiftCount === 1 ? '' : 's'} ·{' '}
            {job.quantity || 1} worker
            {job.quantity === 1 ? '' : 's'} · {hours.toFixed(2)} hrs
          </JobMetaText>
        </JobMeta>
      </JobHeaderRow>

      <ScheduleToggleRow>
        <ScheduleToggleInner type="button" onClick={() => setOpen((o) => !o)}>
          <ScheduleLabel>Schedule</ScheduleLabel>
          <ScheduleAction>
            <span>{open ? 'Hide Schedule' : 'View Schedule'}</span>
            <Chevron open={open}>
              <ChevronIcon />
            </Chevron>
          </ScheduleAction>
        </ScheduleToggleInner>
      </ScheduleToggleRow>

      <Collapsible open={open}>
        <ScheduleTable>
          {sortedShifts.length === 0 ? (
            <ScheduleRowsWrap>
              <ScheduleRow>
                <span>No shifts scheduled</span>
              </ScheduleRow>
            </ScheduleRowsWrap>
          ) : (
            <ScheduleRowsWrap>
              <ScheduleHeaderRow>
                <ScheduleHeaderCell col="date">Date</ScheduleHeaderCell>
                <ScheduleHeaderTime>
                  <ScheduleHeaderTimeLabel>Time</ScheduleHeaderTimeLabel>
                  <ScheduleHeaderTz>EDT</ScheduleHeaderTz>
                </ScheduleHeaderTime>
                <ScheduleHeaderCell col="workers">Workers</ScheduleHeaderCell>
                <ScheduleHeaderCell col="actions">Actions</ScheduleHeaderCell>
              </ScheduleHeaderRow>
              {sortedShifts.map((shift, i) => {
                const qty = shift.quantity || 1;
                const hrs = shiftHours(
                  shift.start_at,
                  shift.end_at,
                  shift.break_minutes,
                );
                const hrsLabel = formatHoursLabel(hrs);
                // `unselected_shift_indexes` and `worker_shift_scope` are
                // keyed off the original `job.shifts` order — the order
                // `buildCreatePayload` walks when emitting per-shift
                // payloads. `sortedShifts` is sorted by start_at for
                // display, so map back to the original index before
                // looking up scope, otherwise the displayed counts won't
                // match what's actually submitted.
                const origIdx = (job.shifts || []).indexOf(shift);
                const isUnselected = (
                  job.unselected_shift_indexes ?? []
                ).includes(origIdx);
                const scope = job.worker_shift_scope ?? {};
                const appliesToShift = (workerId: number) => {
                  const s = scope[workerId];
                  if (s == null) return true;
                  return s.includes(origIdx);
                };
                const hiredCount = isUnselected
                  ? 0
                  : (job.hire_workers ?? []).filter(appliesToShift).length;
                const invitedCount = isUnselected
                  ? 0
                  : (job.invite_workers ?? []).filter(appliesToShift).length;
                return (
                  <ScheduleRow key={i}>
                    <ScheduleDateCell>
                      {toDateLabel(shift.start_at)}
                    </ScheduleDateCell>
                    <ScheduleTimeCell>
                      <ScheduleTimeMain>
                        {formatTime(shift.start_at)} -{' '}
                        {formatTime(shift.end_at)}
                      </ScheduleTimeMain>
                      <ScheduleTimeSub>
                        {breakLabel(shift.break_minutes)}
                      </ScheduleTimeSub>
                      {hrsLabel ? (
                        <ScheduleTimeSub>{hrsLabel}</ScheduleTimeSub>
                      ) : null}
                    </ScheduleTimeCell>
                    <ScheduleWorkersCell>
                      <span>
                        {qty} worker{qty === 1 ? '' : 's'}
                      </span>
                      {hiredCount > 0 || invitedCount > 0 ? (
                        <span
                          style={{ color: '#5B5B5B', fontSize: 13 }}
                          aria-label="Staffing summary"
                        >
                          {hiredCount ? `${hiredCount} hired` : null}
                          {hiredCount && invitedCount ? ' · ' : null}
                          {invitedCount ? `${invitedCount} invited` : null}
                        </span>
                      ) : (
                        <span style={{ color: '#979797' }}>-</span>
                      )}
                    </ScheduleWorkersCell>
                    <ScheduleActions>
                      <IconButton
                        type="button"
                        aria-label="Edit shift"
                        onClick={() => handleOpenEdit(i)}
                      >
                        <PencilIcon />
                      </IconButton>
                      <IconButton
                        type="button"
                        aria-label="Delete shift"
                        onClick={() => handleDeleteShift(i)}
                      >
                        <TrashIcon />
                      </IconButton>
                    </ScheduleActions>
                  </ScheduleRow>
                );
              })}
            </ScheduleRowsWrap>
          )}
        </ScheduleTable>

        <RatesSection>
          <CardHeader>
            <CardTitle>Job rates</CardTitle>
          </CardHeader>
          {/* V1 parity: legacy `JobRates` (non-markup) hides Base Pay rate
              from client admins, but `SummaryTableRevamped` (markup) keeps
              the Pay rate row visible and editable for them. Mirror both
              by gating only the non-markup branch on `!isClientAdmin`. */}
          {isMarkup || !isClientAdmin ? (
            <KeyValueRow>
              <KvLabel>{isMarkup ? 'Pay Rate' : 'Base pay rate'}</KvLabel>
              {payRateEditable ? (
                <RateInputShell>
                  <RateInputDollar>$</RateInputDollar>
                  <RateInput
                    type="text"
                    inputMode="decimal"
                    placeholder="0.00"
                    value={payInput}
                    style={
                      payColor
                        ? { color: payColor, fontWeight: 700 }
                        : undefined
                    }
                    onChange={(event) => setPayInput(event.target.value)}
                    onBlur={() => commitPayRate(payInput)}
                  />
                </RateInputShell>
              ) : (
                <KvValue style={payColor ? { color: payColor } : undefined}>
                  {formatCurrency(centsToDollars(job.pay_rate))}
                </KvValue>
              )}
            </KeyValueRow>
          ) : null}
          {payBelowMin ? (
            <div
              role="alert"
              style={{
                marginTop: 4,
                padding: '8px 12px',
                borderRadius: 6,
                background: '#FDECEC',
                border: '1px solid #F5B7B7',
                color: '#A41B1B',
                fontSize: 13,
                lineHeight: 1.4,
              }}
            >
              <strong>Pay rate is below minimum</strong> — the minimum pay rate
              is {formatCurrency(centsToDollars(minPay))}.
            </div>
          ) : null}
          {!isMarkup ? (
            <KeyValueRow>
              <KvLabel>Base bill rate</KvLabel>
              <KvValue>{formatCurrency(centsToDollars(job.cost_rate))}</KvValue>
            </KeyValueRow>
          ) : null}
          <KeyValueRow>
            <KvLabel>Total hours</KvLabel>
            {/* totalShiftHours already weights each shift by its own
                quantity, so multiplying again by job.quantity here
                would double-count when shifts share the job quantity
                and produce 48 instead of 32 in the 8×1 + 8×3 case. */}
            <KvValue>{hours.toFixed(2)}</KvValue>
          </KeyValueRow>
        </RatesSection>
      </Collapsible>
      {editTarget ? (
        <ShiftPopup
          mode="edit"
          orderTypeLabel="gig"
          dateKey={editTarget.dateKey}
          initial={{
            startTime: editTarget.startTime,
            endTime: editTarget.endTime,
            breakTime: editTarget.breakTime,
            workers: editTarget.workers,
          }}
          position={centeredPopupPosition()}
          onSave={handleSaveEdit}
          onClose={() => setEditTarget(null)}
          pastCheck={
            isClientAdmin
              ? (() => {
                  // V1 parity: anchor "now" to the JOB LOCATION timezone
                  // (legacy `convertTZ(new Date(), address?.timezone)`),
                  // not the browser's, so editing a same-day shift is
                  // gated identically regardless of host timezone.
                  const now = nowInTimezone(
                    resolveJobTimezone(locations, job.address_id),
                  );
                  const pad = (n: number) => String(n).padStart(2, '0');
                  return {
                    todayKey: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`,
                    nowMinutes: now.getHours() * 60 + now.getMinutes(),
                  };
                })()
              : null
          }
        />
      ) : null}
    </Card>
  );
};

const ReviewStep = ({
  orderType,
  jobs,
  skills,
  skillsLoading,
  skillsSettled,
  paymentMethods,
  paymentMethodsLoading,
  paymentMethodsError,
  billingAccountName,
  customerName,
  billingAccount,
  locations = [],
  onUpdateJobs,
  onAddSkill,
  onEditJob,
  surgeRates = [],
  onRatesValidityChange,
  onPaymentMethodValidityChange,
  onSkillsValidityChange,
  onShiftsValidityChange,
}: ReviewStepProps) => {
  const { currentAdminIsCustomerAdmin: isClientAdminTop } = useAuth();
  const isMarkup = billingAccount?.rate_type === 'MARKUP';

  // Missing-payment-method state mirrors legacy CreateGigOrder's Billing
  // form (`!accountPaymentMethods.currentPaymentMethod`). This flag drives
  // the visible "no payment method — click here to add" alert, so it
  // remains scoped to the fetched-and-empty case: we don't want to render
  // that misleading copy when the cause is a transport error (the error
  // surfaces in the Payment method card via `paymentMethodsError` below).
  const paymentMethodMissing =
    !!paymentMethods &&
    !paymentMethodsLoading &&
    !paymentMethodsError &&
    !paymentMethods.current_payment_method;

  // Submit gate matches EntryScreen's strict verification: Place order
  // stays disabled until the /payment_methods fetch has *landed* and
  // confirmed a `current_payment_method`. V1 parity: the legacy Billing
  // form held the action disabled through the in-flight window — it did
  // not optimistically treat loading as verified. Three gates all flip
  // validity to false: loading (result hasn't landed), error (we never
  // confirmed a method, must not be more permissive than a settled
  // empty response), and settled-empty (`current_payment_method` is
  // null/undefined).
  const paymentMethodVerified =
    !paymentMethodsLoading &&
    !paymentMethodsError &&
    !!paymentMethods?.current_payment_method;

  useEffect(() => {
    if (!onPaymentMethodValidityChange) return;
    onPaymentMethodValidityChange(paymentMethodVerified);
  }, [onPaymentMethodValidityChange, paymentMethodVerified]);

  // Deep-link target for the "Click here" CTA. Mirrors the v1 split:
  // client admins manage their own company billing accounts under
  // /my-company, tenant admins manage them under /clients/:customerId.
  // Falls back to null when we lack enough context to build the link, so
  // the warning still renders without a clickable target.
  const accountSettingsPath = (() => {
    if (!billingAccount?.id) return null;
    if (isClientAdminTop) {
      return `/${ROUTES.myCompany}/accounts/${billingAccount.id}`;
    }
    if (billingAccount.customer_id == null) return null;
    return `/${ROUTES.clients}/${billingAccount.customer_id}/accounts/${billingAccount.id}`;
  })();
  const surgeByDate = new Map(surgeRates.map((sr) => [sr.date, sr]));
  // Mirror the legacy SummaryTableRevamped: shifts that fall on a surge
  // date are billed at `cost_rate * surge.rate`. Without this, the review
  // total would understate the estimate for surge-rate customers.
  const surgeAdjustedEstimate = (job: JobDraftV2) => {
    let total = 0;
    for (const shift of job.shifts || []) {
      const start = new Date(shift.start_at).getTime();
      const end = new Date(shift.end_at).getTime();
      let diffMs = end - start;
      if (diffMs <= 0) diffMs += 24 * 3_600_000;
      const hours = Math.max(
        0,
        diffMs / 3_600_000 - (shift.break_minutes || 0) / 60,
      );
      const dateKey = shift.start_at.slice(0, 10);
      const multiplier = surgeByDate.get(dateKey)?.rate ?? 1;
      // Same per-shift-quantity rule as totalShiftHours: a shift with
      // 3 workers costs 3× a 1-worker shift even when the job-level
      // quantity is 1.
      const qty = shift.quantity ?? job.quantity ?? 1;
      total += hours * centsToDollars(job.cost_rate) * multiplier * qty;
    }
    return total;
  };
  const totalHours = jobs.reduce(
    // totalShiftHours is per-shift-quantity weighted — do not multiply
    // again by job.quantity (see the helper docblock above).
    (acc, job) => acc + totalShiftHours(job),
    0,
  );
  const totalEstimate = jobs.reduce(
    (acc, job) => acc + surgeAdjustedEstimate(job),
    0,
  );
  const reviewHolidayDates = jobs.flatMap((job) =>
    (job.shifts || [])
      .map((s) => s.start_at.slice(0, 10))
      .filter((dk) => surgeByDate.has(dk)),
  );
  const uniqueHolidayDates = Array.from(new Set(reviewHolidayDates));

  const headingTarget = customerName ?? billingAccountName ?? null;
  const headingSuffix = headingTarget ? ` for ${headingTarget}` : '';
  const paymentLabel = formatPaymentMethod(paymentMethods);

  // V1 parity (`CreateGigOrder` `FirstOrderNotice`): customer admins see a
  // first-order 50% card-charge notice when the selected billing account
  // pays by credit card and has no prior orders. Mirrors V1's three gates:
  // not a tenant admin, current payment method is one of the account's
  // cards (`checkIfCardIsCurrentPaymentMethod`), and the account has no
  // orders yet (`account_has_orders` from the V2 payment-methods endpoint).
  const currentIsCard =
    !!paymentMethods?.current_payment_method &&
    (paymentMethods.cards ?? []).some(
      (card) => card.id === paymentMethods.current_payment_method,
    );
  const showFirstOrderNotice =
    isClientAdminTop &&
    !paymentMethodsLoading &&
    !paymentMethodsError &&
    !!paymentMethods &&
    currentIsCard &&
    paymentMethods.account_has_orders === false;

  const updateJobAt = (index: number, next: JobDraftV2) => {
    if (!onUpdateJobs) return;
    const copy = [...jobs];
    copy[index] = next;
    onUpdateJobs(copy);
  };

  // Removes a job by index — used as the recovery "Remove this job"
  // action on a job whose saved skill is no longer available. Guarded
  // by `jobs.length > 1` at the call site so we never leave the order
  // with zero jobs (and the JobCard's onRemoveJob is undefined in that
  // case, hiding the button).
  const removeJobAt = (index: number) => {
    if (!onUpdateJobs) return;
    const copy = jobs.filter((_, i) => i !== index);
    onUpdateJobs(copy);
  };

  // Per-job skill availability, aggregated to a single boolean and
  // bubbled up so the wizard footer can disable Submit while any
  // job's saved skill no longer resolves against the loaded skills
  // list. Defaults to "available" so an in-flight skills fetch never
  // false-flags submit during the initial fan-out.
  const [perJobSkillUnavailable, setPerJobSkillUnavailable] = useState<
    Record<number, boolean>
  >({});
  const handleJobSkillAvailability = (index: number, unavailable: boolean) => {
    setPerJobSkillUnavailable((prev) =>
      prev[index] === unavailable ? prev : { ...prev, [index]: unavailable },
    );
  };
  // When a job is removed, prune its entry so a stale `true` doesn't
  // keep submit disabled forever.
  useEffect(() => {
    setPerJobSkillUnavailable((prev) => {
      const next: Record<number, boolean> = {};
      jobs.forEach((_, index) => {
        if (prev[index] !== undefined) next[index] = prev[index]!;
      });
      return next;
    });
  }, [jobs.length]);
  const aggregateSkillsValid = jobs.every(
    (_, index) => perJobSkillUnavailable[index] !== true,
  );
  useEffect(() => {
    if (!onSkillsValidityChange) return;
    onSkillsValidityChange(aggregateSkillsValid);
  }, [onSkillsValidityChange, aggregateSkillsValid]);

  // Per-job "has at least one shift" map, aggregated to a single boolean
  // and bubbled up so the wizard footer can disable Submit while any
  // job carries an empty `shifts: []` (typical of a multi-skill template
  // whose non-first skill was never scheduled). Defaults to "valid" so
  // we never block the user mid-render before the first JobCard mounts
  // and reports.
  const [perJobShiftsValid, setPerJobShiftsValid] = useState<
    Record<number, boolean>
  >({});
  const handleJobShiftsValidity = (index: number, valid: boolean) => {
    setPerJobShiftsValid((prev) =>
      prev[index] === valid ? prev : { ...prev, [index]: valid },
    );
  };
  useEffect(() => {
    setPerJobShiftsValid((prev) => {
      const next: Record<number, boolean> = {};
      jobs.forEach((_, index) => {
        if (prev[index] !== undefined) next[index] = prev[index]!;
      });
      return next;
    });
  }, [jobs.length]);
  const aggregateShiftsValid = jobs.every(
    (_, index) => perJobShiftsValid[index] !== false,
  );
  useEffect(() => {
    if (!onShiftsValidityChange) return;
    onShiftsValidityChange(aggregateShiftsValid);
  }, [onShiftsValidityChange, aggregateShiftsValid]);

  // Per-job validity, aggregated to a single boolean and bubbled up so
  // the wizard footer can disable Submit on any below-minimum pay rate.
  // Missing entries default to `true` (e.g., the rate range hasn't
  // resolved yet) so we never block the user on an in-flight fetch.
  const [perJobValidity, setPerJobValidity] = useState<Record<number, boolean>>(
    {},
  );
  const handleJobValidity = (index: number, valid: boolean) => {
    setPerJobValidity((prev) =>
      prev[index] === valid ? prev : { ...prev, [index]: valid },
    );
  };
  const aggregateValid = jobs.every(
    (_, index) => perJobValidity[index] !== false,
  );
  useEffect(() => {
    if (!onRatesValidityChange) return;
    onRatesValidityChange(aggregateValid);
  }, [onRatesValidityChange, aggregateValid]);

  // Top-of-list banner counting jobs that still have no schedule. The
  // per-card noShifts alert already surfaces above each affected job
  // header, but multi-skill repeats can collapse cards below the fold
  // and a user may miss them; a hoisted summary at the top of the
  // order list makes the gate condition impossible to overlook.
  const jobsWithoutShifts = jobs.filter(
    (job) => (job.shifts || []).length === 0,
  ).length;

  return (
    <PageShell>
      <OrderHeading>
        Order{headingSuffix}
        {orderType ? ` · ${orderType === 'LTA' ? 'LTA' : 'Gig'}` : ''}
      </OrderHeading>

      <TwoColumn>
        <LeftColumn>
          <ScheduleNeededBanner jobsWithoutShifts={jobsWithoutShifts} />
          {jobs.length === 0 ? (
            <Card>
              <JobHeaderRow>
                <JobMeta>
                  <JobName>No jobs configured</JobName>
                  <JobMetaText muted>
                    Go back and pick a skill and schedule to add a job.
                  </JobMetaText>
                </JobMeta>
              </JobHeaderRow>
            </Card>
          ) : (
            jobs.map((job, index) => (
              <JobCard
                key={index}
                job={job}
                index={index}
                skills={skills}
                skillsLoading={skillsLoading}
                skillsSettled={skillsSettled}
                defaultOpen={index === 0}
                isMarkup={isMarkup}
                markupPercent={billingAccount?.markup_percent ?? 0}
                billingAccountId={billingAccount?.id ?? null}
                locations={locations}
                onValidityChange={handleJobValidity}
                onSkillAvailabilityChange={handleJobSkillAvailability}
                onShiftsValidityChange={handleJobShiftsValidity}
                onUpdateJob={
                  onUpdateJobs ? (next) => updateJobAt(index, next) : undefined
                }
                onRemoveJob={
                  onUpdateJobs && jobs.length > 1
                    ? () => removeJobAt(index)
                    : undefined
                }
                onEditJob={onEditJob ? () => onEditJob(index) : undefined}
              />
            ))
          )}

          {onAddSkill ? (
            <AddSkillsButton type="button" onClick={onAddSkill}>
              <PlusIcon />
              Add Skills
            </AddSkillsButton>
          ) : null}
        </LeftColumn>

        <RightColumn>
          <Card>
            <CardHeader>
              <CardTitle>Summary</CardTitle>
            </CardHeader>
            <KeyValueRow>
              <KvLabel>Total hours</KvLabel>
              <KvValue>{totalHours.toFixed(2)}</KvValue>
            </KeyValueRow>
            <KeyValueRow>
              <KvLabel>Total estimate</KvLabel>
              <KvValue>{formatCurrency(totalEstimate)}</KvValue>
            </KeyValueRow>
            {uniqueHolidayDates.length > 0 ? (
              <div
                style={{
                  marginTop: 12,
                  background: '#FFF6E8',
                  border: '1px solid #FFD49A',
                  color: '#7A4309',
                  borderRadius: 6,
                  padding: '8px 10px',
                  fontSize: 12,
                  lineHeight: 1.4,
                }}
              >
                Estimate includes holiday surge multipliers on{' '}
                {uniqueHolidayDates
                  .map((dk) => {
                    const surge = surgeByDate.get(dk);
                    return surge ? `${dk} (${surge.rate}×)` : dk;
                  })
                  .join(', ')}
                .
              </div>
            ) : null}
            <div style={{ height: 16 }} />
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payment method</CardTitle>
            </CardHeader>
            <PaymentBody>
              {paymentMethodsLoading ? (
                <PaymentMuted>Loading payment methods…</PaymentMuted>
              ) : paymentMethodsError ? (
                <PaymentMuted style={{ color: '#A0292B' }}>
                  {paymentMethodsError}
                </PaymentMuted>
              ) : paymentLabel ? (
                <PaymentLabel>{paymentLabel}</PaymentLabel>
              ) : (
                // V1 parity: legacy Billing form renders a danger-colored
                // "There is no payment method associated with this billing
                // account. Click here to add payment method." and disables
                // Start order. The Submit gate is wired in the parent via
                // onPaymentMethodValidityChange so the user can't proceed.
                <div
                  role="alert"
                  style={{
                    padding: '10px 12px',
                    borderRadius: 6,
                    background: '#FDECEC',
                    border: '1px solid #F5B7B7',
                    color: '#A41B1B',
                    fontSize: 13,
                    lineHeight: 1.4,
                  }}
                >
                  <div>
                    <strong>
                      There is no payment method associated with this billing
                      account.
                    </strong>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    {accountSettingsPath ? (
                      <Link
                        to={accountSettingsPath}
                        style={{
                          color: '#A41B1B',
                          fontWeight: 700,
                          textDecoration: 'underline',
                        }}
                      >
                        Click here
                      </Link>
                    ) : (
                      <strong>Click here</strong>
                    )}{' '}
                    to add payment method.
                  </div>
                </div>
              )}
              {showFirstOrderNotice && (
                <div
                  style={{
                    marginTop: 12,
                    padding: '10px 12px',
                    borderRadius: 6,
                    background: '#F5F7FA',
                    border: '1px solid #D9E1EC',
                    color: '#3D4A5C',
                    fontSize: 13,
                    lineHeight: 1.4,
                  }}
                >
                  You will be charged 50% of the estimated total cost when your
                  order is placed. Once the order has been completed and the
                  hours approved, you will be charged the remaining balance.
                </div>
              )}
            </PaymentBody>
          </Card>
        </RightColumn>
      </TwoColumn>
    </PageShell>
  );
};

export default ReviewStep;
